import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Backdrop,
  CircularProgress, Box, TextField, Divider, MenuItem, FormControlLabel, Checkbox, Stack,
  Snackbar, Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { GoogleLogin } from '@react-oauth/google';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { sha256 } from 'js-sha256';

import routes from '../Config/routes';
import client from '../API';
import { niches, region } from '../Utils/constants';
import { useCreatorAuth } from '../Hooks/creator-use-auth';
import MuiPhoneInput from '../Components/MuiPhoneInput';
import AlertDialog from '../Components/AlertDialog';
import useAlertDialog from '../Components/useAlertDialog';
import { replaceConsoleError } from '../Utils/toastNotifications';

/* -------------------------------------------------------------------------- */
/*  Google OAuth constants                                                    */
/* -------------------------------------------------------------------------- */
const GOOGLE_IOS_CLIENT_ID =
  '489265257959-374fsc04dffos29tto4jsqcd2sch5f9o.apps.googleusercontent.com';
const GOOGLE_SCOPES = [
  'https://mail.google.com/',
];
const REDIRECT_URI =
  'com.googleusercontent.apps.489265257959-374fsc04dffos29tto4jsqcd2sch5f9o:/oauth2redirect';

/* -------------------------------------------------------------------------- */
/*  Styled Components                                                         */
/* -------------------------------------------------------------------------- */
const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontWeight: 600,
  padding: '14px 32px',
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '16px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(102, 126, 234, 0.6)',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    transform: 'none',
    boxShadow: 'none',
  }
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: '#e2e8f0',
      borderWidth: '1px',
    },
    '&:hover': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#e2e8f0',
      },
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#667eea',
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
}));

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  backgroundColor: 'rgba(15, 23, 42, 0.9)',
  backdropFilter: 'blur(8px)',
}));

/* -------------------------------------------------------------------------- */
/*  PKCE helpers                                                              */
/* -------------------------------------------------------------------------- */
const base64url = (uint8) =>
  btoa(String.fromCharCode(...uint8))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const createPKCE = () => {
  const rnd = crypto.getRandomValues(new Uint8Array(32));
  const verifier = base64url(rnd);
  const hash = sha256.arrayBuffer(verifier);
  const challenge = base64url(new Uint8Array(hash));
  return { verifier, challenge };
};

const exchangeCodeForIdToken = async (code, codeVerifier) => {
  const body = new URLSearchParams({
    code,
    client_id: GOOGLE_IOS_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    grant_type: 'authorization_code',
    code_verifier: codeVerifier,
  }).toString();

  const res = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error_description || 'Google token exchange failed');
  }
  const data = await res.json();
  if (!data.id_token) throw new Error('No id_token returned from Google');
  return data.id_token;
};

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */
const CreatorOnboard = ({ conversationIdParam, inviteEmailParam, prefilledUsername = '' }) => {
  const navigate = useNavigate();
  const { login: creatorLogin } = useCreatorAuth();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  /* ------------------------------ state ----------------------------------- */
  const [username, setUsername] = useState(prefilledUsername);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [creatorRegion, setCreatorRegion] = useState('');
  const [creatorNiche, setCreatorNiche] = useState([]);
  const [platformHandles, setPlatformHandles] = useState({
    instagram: '', tiktok: '', youtube: '', podcast: '', twitch: '', kick: '', x: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [nicheMenuOpen, setNicheMenuOpen] = useState(false);
  
  const pkceRef = useRef(null);
  const tabOpen = useRef(false);
  const isWeb = Capacitor.getPlatform() === 'web';

  /* ----------------------- helpers / UI feedback -------------------------- */
  const showSnackbar = (message, severity = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  const handleNicheChange = (event) => {
    const value = event.target.value;
    setCreatorNiche(typeof value === 'string' ? value.split(',') : value);
    setNicheMenuOpen(false);
  };

  const alertErr = (msg) => openDialog('Error', msg, closeDialog, closeDialog, 'Ok', null);

  // Rules here mirror the backend validator in
  // blitz-backend/app/creatorUsers/routes.py::_validate_onboard_data
  // (plus the shared region/niche/phone expectations the rest of the
  // creator stack relies on) so users get an immediate, field-specific
  // error instead of a generic "Invalid onboard data" after the network
  // round-trip.
  const onSubmitLocalValidation = () => {
    if (!termsAccepted) {
      showSnackbar('You must accept the Terms & Conditions to continue.');
      return false;
    }
    const trimmedUsername = (username || '').trim();
    if (!trimmedUsername) {
      setValidationError('Username is required.');
      return false;
    }
    if (trimmedUsername.includes(' ')) {
      setValidationError('Creator name cannot contain spaces.');
      return false;
    }
    if (trimmedUsername.length < 3) {
      setValidationError('Username must be at least 3 characters long.');
      return false;
    }
    if (trimmedUsername.length > 100) {
      setValidationError('Username must be 100 characters or fewer.');
      return false;
    }
    // Reserved-word blacklist mirrors the backend's word-boundary check.
    const reservedUsername = /\b(testadmin|admin|root|test|hack|malicious|sql|script|javascript|eval|exec)\b/i;
    if (reservedUsername.test(trimmedUsername)) {
      setValidationError("Username can't contain reserved words like 'admin', 'test', or 'root'. Please choose a different one.");
      return false;
    }
    // Count actual digits so unusual separators don't sneak through.
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 10) {
      showSnackbar('Please enter a valid phone number with country code (at least 10 digits).');
      return false;
    }
    if (!creatorRegion) {
      showSnackbar('Please select a region.');
      return false;
    }
    if (!creatorNiche || creatorNiche.length === 0) {
      showSnackbar('Please select at least one niche.');
      return false;
    }
    const anyHandle = Object.values(platformHandles).some((v) => v && String(v).trim().length > 0);
    if (!anyHandle) {
      showSnackbar('Enter at least one platform username.');
      return false;
    }
    setValidationError('');
    return true;
  };

  /* ------------------------------ API call -------------------------------- */
  const submitCreator = async (googleToken) => {
    if (!onSubmitLocalValidation()) return;
    setLoading(true);

    const payload = {
      username,
      is_partner: false,
      region: creatorRegion,
      niche: creatorNiche,
      google_code: googleToken,
      phone_number: phoneNumber,
      youtube: platformHandles.youtube ? `https://www.youtube.com/@${platformHandles.youtube}` : null,
      instagram: platformHandles.instagram ? `https://www.instagram.com/${platformHandles.instagram}` : null,
      tiktok: platformHandles.tiktok ? `https://www.tiktok.com/@${platformHandles.tiktok}` : null,
      podcast: platformHandles.podcast || null,
      x: platformHandles.x ? `https://www.x.com/${platformHandles.x}` : null,
      twitch: platformHandles.twitch ? `https://www.twitch.tv/${platformHandles.twitch}` : null,
      kick: platformHandles.kick ? `https://www.kick.com/${platformHandles.kick}` : null,
    };

    try {
      const data = await client.creatorConnect.onboard(payload);
      showSnackbar('You have successfully created your CreatorConnect account.', 'success');
      setTimeout(() => {
        if (data?.token && data?.creator_user) {
          creatorLogin({
            token: data.token,
            refresh_token: data.refresh_token,
            creator_user: data.creator_user,
          });
          navigate(routes.creatorConnectEdit);
          return;
        }
        navigate(`${routes.login}?tab=creator`);
      }, 2000);
    } catch (err) {
      console.error(err);
      showSnackbar(`There was an error creating your account: ${err.response?.data?.error || err.message}`);
      setLoading(false);
    }
  };

  /* --------------------------- Web Google flow --------------------------- */
  const webGoogleSuccess = (resp) => submitCreator(resp?.code || resp?.credential);
  const webGoogleFail = (e) => showSnackbar(e.error ?? 'Google sign-in failed. Please try again.');

  /* --------------------------- Native Google flow ------------------------ */
  const nativeGoogle = async () => {
    try {
      pkceRef.current = createPKCE();
      const { challenge } = pkceRef.current;

      tabOpen.current = true;
      setLoading(true);

      await Browser.open({
        url: `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(
          GOOGLE_IOS_CLIENT_ID
        )}&redirect_uri=${encodeURIComponent(
          REDIRECT_URI
        )}&response_type=code&code_challenge=${encodeURIComponent(
          challenge
        )}&code_challenge_method=S256&scope=${encodeURIComponent(GOOGLE_SCOPES.join(' '))}&prompt=select_account`,
        presentationStyle: 'fullscreen',
      });
    } catch (err) {
      console.error('[CreatorOnboard] Native Google launch error:', err);
      showSnackbar(err.message || 'Google login cancelled');
      setLoading(false);
      tabOpen.current = false;
    }
  };

  /* ---------- Deep-link handler (only active inside the iOS app) ---------- */
  const handleDeepLink = useCallback(
    async ({ url }) => {
      try {
        const u = new URL(url);
        if (
          u.protocol.startsWith('com.googleusercontent.apps.') &&
          u.pathname === '/oauth2redirect'
        ) {
          if (tabOpen.current) {
            await Browser.close().catch(() => {});
            tabOpen.current = false;
          }

          const code = u.searchParams.get('code');
          const error = u.searchParams.get('error');
          if (error) throw new Error(`Google OAuth error: ${error}`);
          if (!code) throw new Error('Missing OAuth code');

          const idToken = await exchangeCodeForIdToken(code, pkceRef.current?.verifier);
          await submitCreator(idToken);
        }
      } catch (err) {
        console.error('[CreatorOnboard] Deep link error:', err);
        showSnackbar(err.message || 'Google login failed');
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!isWeb) {
      const urlSub = App.addListener('appUrlOpen', handleDeepLink);
      const browserSub = Browser.addListener('browserFinished', () => {
        tabOpen.current = false;
        setLoading(false);
      });
      return () => {
        urlSub.remove();
        browserSub.remove();
      };
    }
  }, [isWeb, handleDeepLink]);

  /* ------------------------------ UI ------------------------------------- */
  const handleChange = (field) => (e) =>
    setPlatformHandles((s) => ({ ...s, [field]: e.target.value }));

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
      <StyledBackdrop sx={{ zIndex: (t) => t.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" size={60} />
      </StyledBackdrop>

      <AlertDialog alertState={dialogState} />

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity} 
          sx={{ 
            width: '100%',
            borderRadius: '12px',
            '&.MuiAlert-standardError': {
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b'
            },
            '&.MuiAlert-standardSuccess': {
              backgroundColor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              color: '#166534'
            }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Optional disclaimer modal */}
      <Dialog 
        open={disclaimerOpen} 
        onClose={() => setDisclaimerOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: 2
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1e293b' }}>
          Signup Info
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            This signup form creates your Blitz CreatorConnect profile. We secure your account
            with Google OAuth to keep your data safe.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDisclaimerOpen(false)}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------- FORM ------------------------- */}
      <form onSubmit={(e) => e.preventDefault()} noValidate>
        <Stack spacing={4}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', textAlign: 'center' }}>
            Creator Account Setup
          </Typography>

          <ModernTextField
            label="Username"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={!!validationError}
            helperText={validationError || "Use your main platform handle. 3+ characters, no spaces, and avoid reserved words like 'admin' or 'test'."}
            disabled={loading}
            fullWidth
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <ModernTextField
              label="Region"
              select
              required
              value={creatorRegion}
              onChange={(e) => setCreatorRegion(e.target.value)}
              disabled={loading}
              fullWidth
            >
              {region.map((r) => (
                <MenuItem key={r} value={r}>
                  {r === 'NACAUKAUS' ? 'USA, Canada, UK, Australia' : r}
                </MenuItem>
              ))}
            </ModernTextField>

            <ModernTextField
              label="Niche"
              select
              required
              value={creatorNiche}
              SelectProps={{
                multiple: true,
                open: nicheMenuOpen,
                onOpen: () => setNicheMenuOpen(true),
                onClose: () => setNicheMenuOpen(false),
                renderValue: (selected) => selected.join(', '),
                MenuProps: {
                  PaperProps: {
                    sx: { maxHeight: { xs: 320, sm: 420 } },
                  },
                },
              }}
              onChange={handleNicheChange}
              disabled={loading}
              fullWidth
            >
              {niches.map((n) => {
                const selected = creatorNiche.includes(n);

                return (
                  <MenuItem
                    key={n}
                    value={n}
                    selected={selected}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <span>{n}</span>
                    {selected && <CheckIcon fontSize="small" color="primary" />}
                  </MenuItem>
                );
              })}
            </ModernTextField>
          </Box>

          <Box sx={{ '& .react-tel-input': { width: '100%' } }}>
            <MuiPhoneInput
              country="us"
              preferredCountries={['us']}
              value={phoneNumber}
              onChange={setPhoneNumber}
              required
              inputProps={{
                style: {
                  width: '100%',
                  borderRadius: '12px',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  fontSize: '16px',
                  padding: '16px 14px',
                  transition: 'all 0.3s ease'
                }
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
            Platform Handles
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {[
              ['youtube', '@YourChannelName'],
              ['instagram', '@username'],
              ['tiktok', '@yourusername'],
              ['podcast', 'Podcast Name or URL'],
              ['x', '@username'],
              ['kick', '@username'],
              ['twitch', '@username'],
            ].map(([field, hint]) => (
              <ModernTextField
                key={field}
                label={`${field.charAt(0).toUpperCase() + field.slice(1)} Username`}
                value={platformHandles[field]}
                placeholder={hint}
                onChange={handleChange(field)}
                disabled={loading}
                fullWidth
              />
            ))}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={loading}
                sx={{ 
                  color: '#667eea',
                  '&.Mui-checked': { color: '#667eea' }
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                I agree to the{' '}
                <a 
                  href="/terms_creators" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    color: '#667eea', 
                    textDecoration: 'none',
                    fontWeight: 600
                  }}
                >
                  Terms & Conditions
                </a>
              </Typography>
            }
          />

          {/* ------------------- Google Sign-in CTA ------------------- */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
            {isWeb ? (
              <GoogleLogin
                size="large"
                ux_mode="popup"
                flow="auth-code"
                access_type="offline"
                scope={GOOGLE_SCOPES.join(' ')}
                prompt="consent"
                onSuccess={webGoogleSuccess}
                onError={webGoogleFail}
                disabled={!termsAccepted}
              />
            ) : (
              <GradientButton
                onClick={nativeGoogle}
                disabled={!termsAccepted || loading}
                size="large"
                sx={{ py: 1.5, px: 6 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                ) : null}
                Sign up with Google
              </GradientButton>
            )}
          </Box>

          <Typography 
            variant="body2" 
            sx={{ 
              textAlign: 'center', 
              color: 'text.secondary',
              pt: 2,
              borderTop: '1px solid #f1f5f9'
            }}
          >
            By signing up, you'll be able to connect with brands and monetize your content.
          </Typography>
        </Stack>
      </form>
    </Box>
  );
};

export default CreatorOnboard;
